/* eslint-disable react/jsx-no-target-blank */

// React
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'

// Libraries
import { range, map, isEmpty } from 'lodash'
import { Map } from 'immutable'
import { withTranslation } from 'shared/utils/withTranslation'

// Components
import { Product } from './product'
import Pagination from './pagination'
import ProductSearch from './product_search'
import CollectionList from 'store/modules/product/components/CollectionList'
import { ModalCartPicker } from 'store/modules/cart/CartPicker/Modal'

// Shared
import ItemActions from 'store/v2/redux/actions/item'
import { getLocalCartId } from 'shared/utils/cart'

export class ProductListComponent extends Component {
  static displayName = 'ProductList'

  static propTypes = {
    items: PropTypes.object,
    hasItems: PropTypes.bool,
    hasDates: PropTypes.any,
    searchVisible: PropTypes.bool,
    collectionsVisible: PropTypes.bool,
    filterKey: PropTypes.string,
    filter: PropTypes.instanceOf(Map),
    per: PropTypes.any,
    loadingItems: PropTypes.number,
    wrapClass: PropTypes.string,
    limit: PropTypes.any,
    tags: PropTypes.string,
    categories: PropTypes.string,
    collections: PropTypes.string,
    searchValue: PropTypes.string,
    cart: PropTypes.object,
    t: PropTypes.func,
    Trans: PropTypes.func
  }

  constructor (props) {
    super(props)

    this.state = {
      ids: [],
      loading: true,
      loadingItems: 4,
      total: null,
      page: 1,
      per: this.props.per || 24,
      cartModalOpen: false,
      initialLoaded: false
    }

    this.rootRef = React.createRef()
  }

  componentDidMount = () => {
    return this.fetchItems()
  }

  componentDidUpdate = (prevProps) => {
    const { filter } = this.props

    if (filter && !filter.equals(prevProps.filter)) {
      this.resetFilter()
    }
  }

  resetFilter = () => {
    const { filter } = this.props

    this.setState({ page: 1 })

    return this.fetchItems({ ...filter.toJS(), page: 1 })
  }

  fetchItems = (options) => {
    let limit = 0
    const { tags, categories, collections } = this.props
    const { per, page } = this.state

    if (options == null) {
      options = {}
    }

    this.setState({ loading: true })

    const cartId = getLocalCartId()

    const params = {
      cart_id: cartId,
      page: {
        number: page,
        per
      }
    }

    const filter = {}

    if (tags) {
      filter.tag = tags.split(',')
    }

    if (categories) {
      filter.category = categories.split(',')
    }

    if (collections) {
      filter.collection = collections.split(',')
    }

    if (options.page) {
      params.page.number = options.page
    }
    if (per) {
      params.page.per = parseInt(per)
    }
    if (options.q) {
      filter.q = options.q
    }

    if (options.category_id) {
      filter.category_id = options.category_id
    }

    if (options.collection_id) {
      filter.collection_id = options.collection_id
      // Remove collection filter to avoid conflicts
      filter.collection = []
    }

    if (options.filter_by === 'available') {
      filter.available = true
    }

    if (this.props.limit) {
      limit = parseInt(this.props.limit)

      if (!this.props.per) {
        params.page.number = 1
        params.page.per = limit
      }
    }

    return ItemActions.fetchAll({
      include: 'photo',
      filter: !isEmpty(filter) ? filter : undefined,
      sort: options.sort_by,
      apiVersion: 3,
      ...params
    }).then((response) => {
      this.setState({
        ids: map(response.payload.normalizedResponse.entities.items, 'id'),
        total: limit || response.payload.response.meta.total_count,
        loading: false,
        initialLoaded: true,
        loadingItems: Math.min(limit || response.payload.response.meta.total_count, params.per)
      })
    })
  }

  handlePageChange = (page) => {
    this.fetchItems({ page, ...this.props.filter.toJS() })
    this.setState({ page })

    const node = this.rootRef.current

    if (node?.scrollIntoView != null) {
      return node.scrollIntoView()
    } else {
      return window.scrollTo(0, 0)
    }
  }

  handleModalOpen = () => {
    this.setState({ cartModalOpen: true })
  }

  handleModalClose = () => {
    this.setState({ cartModalOpen: false })
  }

  renderList = () => {
    const { t, Trans } = this.props
    const items = this.props.items.getById(this.state.ids)

    if (items.size()) {
      return items.map((item) => {
        return (
          <div className={`booqable-product ${this.props.wrapClass || ''}`} key={item.id} data-id={item.id}>
            <Product id={item.id} />
          </div>
        )
      })
    } else if (this.state.loading) {
      return range(1, this.props.loadingItems || this.state.loadingItems + 1).map((i) => (
        <div key={i} className="booqable-product">
          <Product />
        </div>
      ))
    } else if (this.props.searchValue !== '' || (this.props.hasItems && items.size() === 0)) {
      return <div className="booqable-product-list-notice">{t('user.store.no_products_found_try_filters')}</div>
    } else {
      return (
        <div className="cleanstate">
          {t('user.store.no_products_found')}
          <Trans
            i18nKey="user.store.login_to_add_products_or_use_tags"
            ns="user"
            components={{ Link: <a href="https://login.booqable.com" target="_blank" className="bq-branded-font" /> }}
          />
        </div>
      )
    }
  }

  render () {
    const { t } = this.props
    const { cartModalOpen } = this.state

    return (
      <Fragment>
        <div
          ref={this.rootRef}
          className={`booqable-product-list-wrapper ${
            this.props.collectionsVisible ? 'booqable-product-list-wrapper-with-sidebar' : undefined
          }`}
        >
          {this.props.searchVisible && <ProductSearch filterKey={this.props.filterKey} />}
          {!this.props.hasDates && (
            <div className="booqable-product-list-notice clickable" onClick={this.handleModalOpen}>
              {t('user.store.no_dates_set')}
            </div>
          )}
          <div className="booqable-product-list-grid">
            {this.renderList()}
          </div>
          {this.state.initialLoaded && this.state.total > 0 && (
            <Pagination
              total={this.state.total}
              page={this.state.page}
              per={this.state.per}
              onChange={this.handlePageChange}
            />
          )}
          {this.props.collectionsVisible && <CollectionList filterKey={this.props.filterKey} />}
        </div>
        <ModalCartPicker open={cartModalOpen} onClose={this.handleModalClose} />
      </Fragment>
    )
  }
}

const mapStateToProps = (state, props) => {
  const searchVisible = props.showSearch === 'true'
  const collectionsVisible = props.showCollections === 'true' || props.showCategories === 'true'
  const cart = state.orm.carts.getById(state.client.cartId)
  const filterKey = props.filterKey || 'global'

  return {
    hasDates: cart && cart.starts_at && cart.stops_at,
    hasItems: state.orm.items.size() > 0,
    items: state.orm.items.getAll(),
    loadingItems: parseInt(props.loadingItems) || parseInt(props.limit),
    searchVisible,
    collectionsVisible,
    filterKey,
    filter: state.filter.get(filterKey),
    cart
  }
}

const reduxComponent = reduxConnect(mapStateToProps, null)(ProductListComponent)

const TranslatedComponent = withTranslation('user')(reduxComponent)

export { TranslatedComponent as ProductList }
